import type { ParserRule, ReactOutputRule } from 'simple-markdown';
import { defaultRules } from 'simple-markdown';

/**
 * A custom `SimpleMarkdown` `text` rule that splits text on URLs, emails, and mentions
 * This allows URLs, emails, and mentions to be matched by their respective rules
 * @param includeMentions Whether to split on mention patterns (should match whether mention rules are included)
 * @returns A `SimpleMarkdown` `text` rule
 */
export const buildText = (includeMentions = false) => {
  return {
    ...defaultRules.text,
    match: (source: string) => {
      try {
        // Defensive: validate input
        if (typeof source !== 'string' || source.length === 0) {
          return null;
        }

        // Match text up to the next URL, email, or mention
        // This allows URLs, emails, and mentions to be matched by their respective rules
        const urlPattern =
          /(?:https?:\/\/)?(?:www\.)?[\w-]+(?:\.[\w-]+)+(?:\/[^\s]*)?/;
        const emailPattern = /[\w.+-]+@[\w.-]+\.[a-z]{2,6}/;
        // Mention patterns: @pr{number} for user mentions, @here for keyword mentions
        const mentionPattern = /@(?:pr\d+|here)/;

        const urlMatch = source.match(urlPattern);
        const emailMatch = source.match(emailPattern);
        // Only split on mentions if mention rules are included
        const mentionMatch = includeMentions
          ? source.match(mentionPattern)
          : null;

        let endIndex = source.length;

        if (urlMatch && urlMatch.index !== undefined) {
          const beforeMatch = source.substring(0, urlMatch.index);
          const afterMatch = source.substring(
            urlMatch.index + urlMatch[0].length
          );

          // Only treat as URL if:
          // 1. Not preceded by @ (would be part of an email)
          // 2. Not followed by @ (would be part of an invalid email-like pattern)
          const isPrecededByAt = /@\s*$/.test(beforeMatch);
          const isFollowedByAt = afterMatch.startsWith('@');

          if (!isPrecededByAt && !isFollowedByAt) {
            endIndex = Math.min(endIndex, urlMatch.index);
          } else {
            // First URL match was invalid (part of email-like pattern).
            // Try to find another valid URL in the remaining text.
            const nextUrlMatch = afterMatch.match(urlPattern);
            if (nextUrlMatch?.index !== undefined) {
              const nextIndex =
                urlMatch.index + urlMatch[0].length + nextUrlMatch.index;
              const nextAfter = source.substring(
                nextIndex + nextUrlMatch[0].length
              );
              // Validate this URL isn't also part of an invalid email-like pattern
              if (!nextAfter.startsWith('@')) {
                endIndex = Math.min(endIndex, nextIndex);
              }
            }
          }
        }

        if (emailMatch && emailMatch.index !== undefined) {
          endIndex = Math.min(endIndex, emailMatch.index);
        }

        if (mentionMatch && mentionMatch.index !== undefined) {
          endIndex = Math.min(endIndex, mentionMatch.index);
        }

        if (endIndex === 0) {
          return null;
        }

        return [source.substring(0, endIndex)];
      } catch {
        // Fallback: return entire source as plain text
        // Ensures the parser always advances and never crashes
        return [source];
      }
    },
  } as ParserRule & ReactOutputRule;
};
